import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { PrimeReactProvider } from "primereact/api";

// Import helper functions and components
import * as ACTION from "../../services/actions";
import StatusTimeline from "../../components/StatusTimeline";
import FilterPanel from "../../components/FilterPanel";
import {
  copyToClipboard,
  toLocale,
  formatAmount,
  getStatusSeverity,
  validateDateRange,
  exportToExcel,
  formatDateForAPI,
} from "../../components/paymentUtils";

// PrimeReact CSS
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

// Helper function to get last 2 months date range
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 2); // 2 months back

  // Set to midnight for consistent filtering
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    start_date: startDate,
    end_date: endDate,
  };
};

// --- UPDATED DATA MAPPING CONFIGURATION BASED ON YOUR API RESPONSE ---
const DATA_MAPPING = {
  // API Response Structure Mapping (matches your response structure)
  response: {
    dataPath: "results", // Direct path to results array
    countPath: "count", // Direct path to count
    fallbackDataPath: "data.results", // Fallback if needed
    fallbackCountPath: "data.count", // Fallback if needed
  },

  // Table Column Field Mapping (updated to match your API fields)
  fields: {
    orderId: "BankRRN", // Transaction ID
    paymentAmount: "reciept_amount", // Amount from receipt
    paymentStatus: "TxnStatus", // SUCCESS/FAILURE status
    customerNumber: "PayerMobile", // Customer phone number
    customerId: "PayerName", // Customer name
    loanNumber: "merchantTranId", // Loan/merchant transaction ID
    createdAt: "TxnCompletionDate", // Transaction completion date
    paymentCompletionTime: "reciept_date", // Receipt date
    modifiedDate: "modified_date", // Modified date
    paymentMethod: "PayerVA", // UPI VPA
    cfPaymentId: "BankRRN", // Bank reference number
    bankReference: "cbs_status", // CBS status
    receiptNo: "reciept_no", // Receipt number
    paymentMessage: "message", // Message field
    errorDescription: "ResponseCode", // Response code
    customerNote: "PayerName", // Payer name
    orderCreatedAt: "TxnInitDate", // Transaction initiation date
    payerAmount: "PayerAmount", // Original payer amount
    responseCode: "ResponseCode", // Response code
    payerAccountType: "PayerAccountType", // Account type
    terminalId: "terminalId", // Terminal ID
    merchantId: "merchantId", // Merchant ID
    attempt: "attempt", // Attempt number
  },

  // Nested Field Mapping (for complex objects) - may not be needed for your API
  nested: {
    // Your API doesn't seem to have nested objects, but keeping for compatibility
    upiId: "PayerVA", // UPI VPA is directly available
    upiChannel: "PayerVA", // Same as UPI VPA
  },

  // Filter Field Mapping (for API requests) - Date range handled separately
  filterMapping: {
    order_id: "BankRRN__icontains", // Search in Bank RRN
    customer_number: "PayerMobile__icontains", // Search in mobile
    customer_id: "PayerName__icontains", // Search in name
    loan_number: "merchantTranId__icontains", // Search in merchant transaction ID
    payment_status: "TxnStatus", // Exact match for status
    cf_payment_id: "BankRRN__icontains", // Search in Bank RRN
    bank_reference: "cbs_status", // Exact match for CBS status
    min_amount: "reciept_amount__gte", // Amount greater than or equal
    max_amount: "reciept_amount__lte", // Amount less than or equal
    // Date range handled separately in buildFilterByObject
  },
};

// --- UTILITY FUNCTIONS ---
/**
 * Gets nested value from object using dot notation
 */
const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;

  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

/**
 * Extracts data from API response using configured mapping
 */
const extractResponseData = (apiResponse) => {
  if (!apiResponse) return { records: [], totalRecords: 0 };

  // Try primary data path first, then fallback
  const records =
    getNestedValue(apiResponse, DATA_MAPPING.response.dataPath) ||
    getNestedValue(apiResponse, DATA_MAPPING.response.fallbackDataPath) ||
    [];

  const totalRecords =
    getNestedValue(apiResponse, DATA_MAPPING.response.countPath) ||
    getNestedValue(apiResponse, DATA_MAPPING.response.fallbackCountPath) ||
    0;

  return { records, totalRecords };
};

/**
 * Gets field value using mapping configuration
 */
const getFieldValue = (rowData, fieldKey, defaultValue = "N/A") => {
  const fieldPath =
    DATA_MAPPING.fields[fieldKey] || DATA_MAPPING.nested[fieldKey];
  return getNestedValue(rowData, fieldPath, defaultValue);
};

// --- Constants with Default Date Range ---
const initialLazyState = {
  first: 0,
  rows: 100,
  page: 0,
  sortField: null,
  sortOrder: null,
  filters: {
    order_id: "",
    customer_number: "",
    customer_id: "",
    loan_number: "",
    payment_status: null,
    cf_payment_id: "",
    bank_reference: "",
    min_amount: null,
    max_amount: null,
    ...getDefaultDateRange(), // 👈 Add default 2-month range here
  },
};

// --- Main Component ---
export default function FlexibleDataTable({
  // Configuration props for customization
  dataMapping = DATA_MAPPING,
  apiEndpoint = "transactions/0/",
  actionType = ACTION.TRANSACTIONLIST,
  columns = null, // Custom column configuration
  title = "Transactions",
  exportEnabled = true,
  repostEnabled = true,
}) {
  const dispatch = useDispatch();
  const { data: apiResponse, loading } = useSelector(
    (state) => state?.transactionlistState || {}
  );
  const toast = useRef(null);
  const dataTableRef = useRef(null);

  const [isTimelineVisible, setTimelineVisible] = useState(false);
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [isFilterPanelVisible, setFilterPanelVisible] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const [lazyState, setLazyState] = useState(initialLazyState);
  const [filters, setFilters] = useState(initialLazyState.filters);

  // PrimeReact configuration
  const primeReactConfig = {
    ripple: false,
    inputStyle: "outlined",
    locale: "en",
    appendTo: "self",
    hideOverlaysOnDocumentScrolling: false,
    nonce: undefined,
    nullSortOrder: 1,
    autoZIndex: true,
    baseZIndex: 0,
    showTransitionOptions: "0.12s cubic-bezier(0, 0, 0.2, 1)",
    hideTransitionOptions: "0.12s cubic-bezier(0, 0, 0.2, 1)",
  };

  // Memoize data extraction using flexible mapping
  const { records: paymentRecords, totalRecords } = React.useMemo(() => {
    return extractResponseData(apiResponse);
  }, [apiResponse]);

  //Dynamic Height Based on Viewport
  const [tableHeight, setTableHeight] = useState("60vh");

  useEffect(() => {
    const updateTableHeight = () => {
      const vh = window.innerHeight;
      setTableHeight(`${vh * 0.6}px`); // 60% of viewport height
    };

    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, []);

  /**
   * Builds the filter_by object with special handling for date range
   */
  const buildFilterByObject = useCallback(
    (currentFilters) => {
      const filterBy = {};

      if (currentFilters && typeof currentFilters === "object") {
        // Handle date range specially - convert to the range format your API expects
        if (currentFilters.start_date && currentFilters.end_date) {
          filterBy["TxnCompletionDate__range"] = [
            formatDateForAPI(currentFilters.start_date),
            formatDateForAPI(currentFilters.end_date),
          ];
        }

        // Handle all other filters (excluding date fields)
        Object.entries(currentFilters).forEach(([key, value]) => {
          // Skip date fields as they're handled above
          if (key === "start_date" || key === "end_date") return;

          if (value !== null && value !== "" && value !== undefined) {
            const mappedField = dataMapping.filterMapping[key];
            if (mappedField) {
              filterBy[mappedField] = value;
            }
          }
        });
      }

      return filterBy;
    },
    [dataMapping.filterMapping]
  );

  /**
   * Loads data from API with flexible endpoint and parameters
   */
  const loadLazyData = useCallback(
    (state, forExport = false) => {
      const {
        page = 0,
        rows = 100,
        sortField = null,
        sortOrder = null,
        filters: currentFilters = {},
      } = state || {};

      const queryParams = new URLSearchParams();

      // For export, fetch all data up to a large page size
      if (forExport) {
        queryParams.append("page_size", "10000");
      } else {
        // Regular pagination - API uses 1-based page indexing
        const apiPage = page + 1;
        if (apiPage > 1) queryParams.append("page", apiPage.toString());
        // Always set page_size for consistency
        queryParams.append("page_size", rows.toString());
      }

      // Sorting - handle API format for ordering
      if (sortField && sortOrder !== null) {
        const sortPrefix = sortOrder === -1 ? "-" : "";
        queryParams.append("ordering", `${sortPrefix}${sortField}`);
      }

      // Build filter_by object using flexible mapping
      const filterBy = buildFilterByObject(currentFilters);

      // Add filter_by parameter if there are any filters
      if (Object.keys(filterBy).length > 0) {
        queryParams.append("filter_by", JSON.stringify(filterBy));
      }

      // Validate date range for export
      if (forExport && currentFilters.start_date && currentFilters.end_date) {
        if (
          !validateDateRange(currentFilters.start_date, currentFilters.end_date)
        ) {
          toast.current?.show({
            severity: "error",
            summary: "Invalid Date Range",
            detail: "Date range cannot exceed 3 months for export",
            life: 3000,
          });
          setIsExporting(false);
          return;
        }
      }

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `${apiEndpoint}?${queryString}`
        : apiEndpoint;

      console.log("API Endpoint:", endpoint);
      console.log("Filter By Object:", filterBy);

      // ✅ FIXED: Use same action type for both regular and export requests
      const actionTypeToUse = `${actionType}_GET`;

      dispatch({
        type: actionTypeToUse,
        payload: endpoint,
        skipAuth: true,
        callback: (response, err) => {
          console.log("API full response:", response);

          if (err || !response) {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: `Failed to ${forExport ? "export" : "load"} data.`,
              life: 3000,
            });
            if (forExport) setIsExporting(false);
            return;
          }

          const { records, totalRecords: count } =
            extractResponseData(response);

          console.log("Extracted data:", {
            records: records.length,
            totalRecords: count,
          });

          if (forExport) {
            // ✅ FIXED: Check if records exist before exporting
            if (records && records.length > 0) {
              exportToExcel(records, currentFilters, toast);
            } else {
              toast.current?.show({
                severity: "warn",
                summary: "No Data",
                detail: "No data available to export",
                life: 3000,
              });
            }
            setIsExporting(false);
          }

          console.log(`Fetched ${records.length} results (of ${count} total)`);
        },
      });
    },
    [dispatch, buildFilterByObject, apiEndpoint, actionType]
  );

  // Initial data load and subsequent loads on lazyState change
  useEffect(() => {
    loadLazyData(lazyState);
  }, [lazyState, loadLazyData]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    if (
      filters.start_date &&
      filters.end_date &&
      !validateDateRange(filters.start_date, filters.end_date)
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid Date Range",
        detail: "Date range cannot exceed 3 months",
        life: 3000,
      });
      return;
    }

    console.log("Applying filters:", filters);
    setLazyState((prev) => ({ ...prev, page: 0, first: 0, filters }));
    toast.current?.show({
      severity: "info",
      summary: "Filters Applied",
      detail: "Data filtered successfully",
      life: 2000,
    });
    // Scroll the DataTable element into view when filters are applied
    if (dataTableRef.current && dataTableRef.current.getElement()) {
      dataTableRef.current
        .getElement()
        .scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [filters]);

  const clearFilters = useCallback(() => {
    console.log("Clearing filters");
    const clearedFilters = {
      order_id: "",
      customer_number: "",
      customer_id: "",
      loan_number: "",
      payment_status: null,
      cf_payment_id: "",
      bank_reference: "",
      min_amount: null,
      max_amount: null,
      start_date: null,
      end_date: null,
    };

    setFilters(clearedFilters);
    setLazyState((prev) => ({
      ...prev,
      page: 0,
      first: 0,
      filters: clearedFilters,
    }));
    toast.current?.show({
      severity: "info",
      summary: "Filters Cleared",
      detail: "All filters have been cleared - Loading all data",
      life: 2000,
    });
    // Scroll the DataTable element into view when filters are cleared
    if (dataTableRef.current && dataTableRef.current.getElement()) {
      dataTableRef.current
        .getElement()
        .scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Add reset to default 2-month filter
  const resetToDefaultFilter = useCallback(() => {
    const defaultFilters = initialLazyState.filters;
    setFilters(defaultFilters);
    setLazyState((prev) => ({
      ...prev,
      page: 0,
      first: 0,
      filters: defaultFilters,
    }));
    toast.current?.show({
      severity: "info",
      summary: "Reset to Default",
      detail: "Applied default 2-month filter",
      life: 2000,
    });
  }, []);

  // ✅ FIXED: Enhanced export function with debugging
  const handleExport = useCallback(() => {
    console.log("🔍 Export clicked - Debug info:");
    console.log("exportEnabled:", exportEnabled);
    console.log("filters:", filters);
    console.log("start_date:", filters.start_date);
    console.log("end_date:", filters.end_date);

    if (!exportEnabled) {
      console.log("❌ Export disabled");
      return;
    }

    if (!filters.start_date || !filters.end_date) {
      console.log("❌ Missing date range");
      toast.current?.show({
        severity: "warn",
        summary: "Date Range Required",
        detail: "Please select both start and end dates for export",
        life: 3000,
      });
      return;
    }

    if (!validateDateRange(filters.start_date, filters.end_date)) {
      console.log("❌ Invalid date range");
      toast.current?.show({
        severity: "error",
        summary: "Invalid Date Range",
        detail: "Date range cannot exceed 3 months for export",
        life: 3000,
      });
      return;
    }

    console.log("✅ Validation passed, showing confirmation dialog");

    confirmDialog({
      message: "Are you sure you want to export the filtered data to Excel?",
      header: "Export Confirmation",
      icon: "pi pi-question-circle",
      accept: () => {
        console.log("✅ Export confirmed, starting export...");
        setIsExporting(true);
        const exportState = { ...lazyState, filters, page: 0, first: 0 };
        loadLazyData(exportState, true);
      },
      reject: () => {
        console.log("❌ Export cancelled by user");
      }
    });
  }, [filters, lazyState, loadLazyData, exportEnabled]);

  const onPageChange = useCallback((event) => {
    console.log("Page change event:", event);
    setLazyState((prev) => ({ ...prev, ...event }));
    // Scroll the DataTable element into view when page changes
    if (dataTableRef.current && dataTableRef.current.getElement()) {
      dataTableRef.current
        .getElement()
        .scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const onSortChange = useCallback((event) => {
    console.log("Sort change event:", event);
    setLazyState((prev) => ({ ...prev, ...event }));
    // Scroll the DataTable element into view when sort changes
    if (dataTableRef.current && dataTableRef.current.getElement()) {
      dataTableRef.current
        .getElement()
        .scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleViewClick = useCallback((row) => {
    setSelectedPayment(row);
    setTimelineVisible(true);
  }, []);

  const handleDetailsClick = useCallback((row) => {
    setSelectedPayment(row);
    setDetailsVisible(true);
  }, []);

  const handleRepostClick = useCallback(
    (row) => {
      if (!repostEnabled) return;

      const orderId = getFieldValue(row, "orderId");
      dispatch({
        type: `${ACTION.RECEIPT}_POST`,
        payload: `reciept/post/${orderId}/`,
        callback: (resp, err) => {
          if (!err) {
            toast.current?.show({
              severity: "success",
              summary: "Success",
              detail: "Payment reposted successfully",
              life: 3000,
            });
            loadLazyData(lazyState);
          } else {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to repost payment.",
              life: 3000,
            });
          }
        },
      });
    },
    [dispatch, lazyState, loadLazyData, repostEnabled]
  );

  // --- Body Templates (Updated with font sizing) ---
  const statusBodyTemplate = useCallback((rowData) => {
    const status = getFieldValue(rowData, "paymentStatus");
    return (
      <Tag
        value={status}
        severity={getStatusSeverity(status)}
        style={{ fontSize: "0.75rem" }}
      />
    );
  }, []);

  const amountBodyTemplate = useCallback((rowData) => {
    // Use reciept_amount if available, otherwise PayerAmount
    const amount =
      getFieldValue(rowData, "paymentAmount") ||
      getFieldValue(rowData, "payerAmount");
    return <span style={{ fontSize: "0.75rem" }}>{formatAmount(amount)}</span>;
  }, []);

  const createdAtBodyTemplate = useCallback((rowData) => {
    const createdAt = getFieldValue(rowData, "createdAt");
    return <span style={{ fontSize: "0.75rem" }}>{toLocale(createdAt)}</span>;
  }, []);

  const paymentCompletionTimeBodyTemplate = useCallback((rowData) => {
    const completionTime = getFieldValue(rowData, "paymentCompletionTime");
    return (
      <span style={{ fontSize: "0.75rem" }}>
        {completionTime && completionTime !== "N/A" ? completionTime : "N/A"}
      </span>
    );
  }, []);

  const customerNumberBodyTemplate = useCallback(
    (rowData) => (
      <span style={{ fontSize: "0.75rem" }}>
        {getFieldValue(rowData, "customerNumber")}
      </span>
    ),
    []
  );

  const bankReferenceBodyTemplate = useCallback(
    (rowData) => (
      <span style={{ fontSize: "0.75rem" }}>
        {getFieldValue(rowData, "bankReference")}
      </span>
    ),
    []
  );

  const loanNumberBodyTemplate = useCallback(
    (rowData) => (
      <span style={{ fontSize: "0.75rem" }}>
        {getFieldValue(rowData, "loanNumber")}
      </span>
    ),
    []
  );

  const orderIdBodyTemplate = useCallback((rowData) => {
    const orderId = getFieldValue(rowData, "orderId");
    return (
      <div className="flex align-items-center gap-2">
        <span style={{ fontSize: "0.75rem" }}>{orderId}</span>
        <Button
          icon="pi pi-copy"
          rounded
          raised
          text
          severity="secondary"
          className="p-button-text p-button-sm p-button-rounded"
          style={{
            fontSize: "0.625rem",
            padding: "0.5rem",
            width: "1.25rem",
            height: "1.25rem",
            color: "#757575",
            opacity: 0.8,
            margin: "0.50rem",
            minWidth: "auto",
          }}
          onClick={() => copyToClipboard(orderId, toast)}
          tooltip="Copy Transaction ID"
          tooltipOptions={{ position: "top", fontSize: "0.25rem" }}
        />
      </div>
    );
  }, []);

  // ✅ FIXED: Added font styling to missing templates
  const receiptIdBodyTemplate = useCallback((rowData) => {
    const receiptNo = getFieldValue(rowData, "receiptNo");
    return (
      <span style={{ fontSize: "0.75rem" }}>
        {receiptNo && receiptNo !== "N/A" && receiptNo !== "" ? receiptNo : "N/A"}
      </span>
    );
  }, []);

  const paymentMethodBodyTemplate = useCallback((rowData) => {
    const payerVA = getFieldValue(rowData, "paymentMethod");
    return (
      <span style={{ fontSize: "0.75rem" }}>
        {payerVA && payerVA !== "N/A" ? `UPI: ${payerVA}` : "N/A"}
      </span>
    );
  }, []);

  const responseCodeBodyTemplate = useCallback((rowData) => {
    const responseCode = getFieldValue(rowData, "responseCode");
    const severity =
      responseCode === "00"
        ? "success"
        : responseCode === "U30"
        ? "danger"
        : "warning";
    return (
      <Tag 
        value={responseCode} 
        severity={severity} 
        style={{ fontSize: "0.75rem" }}
      />
    );
  }, []);

  const repostViewBodyTemplate = useCallback(
    (rowData) => {
      const status = getFieldValue(rowData, "paymentStatus");
      const receiptNo = getFieldValue(rowData, "receiptNo");
      const isSuccess = status === "SUCCESS";
      const hasReceiptNo =
        receiptNo !== "N/A" &&
        receiptNo !== null &&
        receiptNo !== undefined &&
        receiptNo !== "";

      if (isSuccess && !hasReceiptNo && repostEnabled) {
        return (
          <Button
            label="Repost"
            style={{ fontSize: "0.625rem", padding: "0.25rem 0.5rem" }}
            icon="pi pi-refresh"
            className="p-button-sm p-button-success"
            onClick={() => handleRepostClick(rowData)}
            tooltip="Repost Payment"
          />
        );
      } else {
        return (
          <Button
            label="View"
            style={{ fontSize: "0.625rem", padding: "0.25rem 0.5rem" }}
            icon="pi pi-info-circle"
            className="p-button-sm p-button-outlined"
            onClick={() => handleDetailsClick(rowData)}
            tooltip="View Details"
          />
        );
      }
    },
    [handleRepostClick, handleDetailsClick, repostEnabled]
  );

  const actionsBodyTemplate = useCallback(
    (rowData) => {
      return (
        <div className="flex gap-1">
          <Button
            icon="pi pi-eye"
            className="p-button-rounded p-button-text"
            style={{ fontSize: '0.625rem', padding: '0.25rem', width: '1.5rem', height: '1.5rem' }}
            onClick={() => handleViewClick(rowData)}
            tooltip="View Timeline"
          />
        </div>
      );
    },
    [handleViewClick]
  );

  const header = (
    <div>
      <Toolbar
        className="mt-1"
        style={{
          padding: "0.375rem 0.75rem",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "3rem",
          boxSizing: "border-box"
        }}
        start={
          <div className="flex align-items-center" style={{ 
            flex: '1 1 auto', 
            minWidth: 0,
            marginRight: '1rem'
          }}>
            <h4 className="m-0" style={{ 
              fontSize: '1rem', 
              margin: 0,
              fontWeight: '600',
              color: '#374151',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {title}
            </h4>
          </div>
        }
        end={
          <div 
            className="flex align-items-center" 
            style={{ 
              gap: '0.5rem',
              flex: '0 0 auto',
              flexShrink: 0
            }}
          >
            <Button
              style={{
                fontSize: "0.75rem",
                padding: "0.375rem 0.75rem",
                minHeight: "2rem",
                minWidth: 'auto',
                whiteSpace: 'nowrap'
              }}
              label="Reset Default"
              icon="pi pi-refresh"
              onClick={resetToDefaultFilter}
              className="p-button-secondary p-button-outlined"
              tooltip="Reset to last 2 months filter"
            />
            <Button
              style={{
                fontSize: "0.75rem",
                padding: "0.375rem 0.75rem",
                minHeight: "2rem",
                minWidth: 'auto',
                whiteSpace: 'nowrap'
              }}
              label="Search & Filter"
              icon="pi pi-filter"
              onClick={() => setFilterPanelVisible(!isFilterPanelVisible)}
              className="p-button-secondary p-button-outlined"
            />
          </div>
        }
      />

      <FilterPanel
        visible={isFilterPanelVisible}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClear={clearFilters}
        onApply={applyFilters}
        onExport={exportEnabled ? handleExport : undefined}
        isExporting={isExporting}
        toastRef={toast}
      />
    </div>
  );

  const renderPaymentDetails = useCallback(
    () =>
      selectedPayment && (
        <div className="p-fluid">
          <Card title="Transaction Information" className="mb-3">
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Transaction ID:</strong>{" "}
              {getFieldValue(selectedPayment, "orderId")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Merchant Transaction ID:</strong>{" "}
              {getFieldValue(selectedPayment, "loanNumber")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Amount:</strong>{" "}
              {formatAmount(getFieldValue(selectedPayment, "paymentAmount"))}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Status:</strong>{" "}
              <Tag
                value={getFieldValue(selectedPayment, "paymentStatus")}
                severity={getStatusSeverity(
                  getFieldValue(selectedPayment, "paymentStatus")
                )}
                style={{ fontSize: '0.75rem' }}
              />
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Response Code:</strong>{" "}
              {getFieldValue(selectedPayment, "responseCode")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>CBS Status:</strong>{" "}
              {getFieldValue(selectedPayment, "bankReference")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Receipt No:</strong>{" "}
              {getFieldValue(selectedPayment, "receiptNo")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Receipt Date:</strong>{" "}
              {getFieldValue(selectedPayment, "paymentCompletionTime")}
            </p>
          </Card>
          <Card title="Customer Information" className="mb-3">
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Payer Name:</strong>{" "}
              {getFieldValue(selectedPayment, "customerId")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Payer Mobile:</strong>{" "}
              {getFieldValue(selectedPayment, "customerNumber")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Account Type:</strong>{" "}
              {getFieldValue(selectedPayment, "payerAccountType")}
            </p>
          </Card>
          <Card title="Payment Method" className="mb-3">
            <p style={{ fontSize: '0.75rem' }}>
              <strong>UPI VPA:</strong>{" "}
              {getFieldValue(selectedPayment, "paymentMethod")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Terminal ID:</strong>{" "}
              {getFieldValue(selectedPayment, "terminalId")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Merchant ID:</strong>{" "}
              {getFieldValue(selectedPayment, "merchantId")}
            </p>
          </Card>
          <Card title="Timing Information">
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Transaction Initiation:</strong>{" "}
              {getFieldValue(selectedPayment, "orderCreatedAt")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Transaction Completion:</strong>{" "}
              {getFieldValue(selectedPayment, "createdAt")}
            </p>
            <p style={{ fontSize: '0.75rem' }}>
              <strong>Attempt:</strong>{" "}
              {getFieldValue(selectedPayment, "attempt")}
            </p>
          </Card>
        </div>
      ),
    [selectedPayment]
  );

  const emptyMessage = loading ? "Loading data..." : "No data found.";

  // Default columns updated to match your API response
  const defaultColumns = [
    {
      field: "BankRRN",
      header: "Transaction ID",
      body: orderIdBodyTemplate,
      sortable: true,
      style: { width: "10%" },
    },
    {
      field: "merchantTranId",
      header: "Loan Number",
      body: loanNumberBodyTemplate,
      sortable: true,
      style: { width: "10%" },
    },
    {
      field: "reciept_amount",
      header: "Amount",
      body: amountBodyTemplate,
      sortable: true,
      style: { width: "8%" },
    },
    {
      field: "TxnStatus",
      header: "Status",
      body: statusBodyTemplate,
      sortable: true,
      style: { width: "8%" },
    },
    {
      field: "TxnCompletionDate",
      header: "Transaction Date",
      body: createdAtBodyTemplate,
      sortable: true,
      style: { width: "10%" },
    },
    {
      field: "reciept_no",
      header: "Receipt Number",
      body: receiptIdBodyTemplate,
      sortable: true,
      style: { width: "10%" },
    },
    {
      field: "reciept_date",
      header: "Receipt Date",
      body: paymentCompletionTimeBodyTemplate,
      sortable: true,
      style: { width: "8%" },
    },
    {
      field: "cbs_status",
      header: "CBS Status",
      body: bankReferenceBodyTemplate,
      sortable: true,
      style: { width: "8%" },
    },
    {
      field: "PayerVA",
      header: "UPI VPA",
      body: paymentMethodBodyTemplate,
      style: { width: "12%" },
    },
    {
      header: "Details",
      body: repostViewBodyTemplate,
      exportable: false,
      style: { minWidth: "8rem", width: "6%" },
    },
    {
      header: "Actions",
      body: actionsBodyTemplate,
      exportable: false,
      style: { minWidth: "6rem", width: "4%" },
    },
  ];

  const columnsToRender = columns || defaultColumns;

  return (
    <PrimeReactProvider value={primeReactConfig}>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card p-2 m-2">
        {header}
        <DataTable
          ref={dataTableRef}
          tableStyle={{ minWidth: "10rem", fontSize: "0.75rem" }}
          value={paymentRecords}
          loading={loading}
          lazy
          paginator
          first={lazyState.first}
          rows={lazyState.rows}
          totalRecords={totalRecords}
          onPage={onPageChange}
          onSort={onSortChange}
          sortField={lazyState.sortField}
          sortOrder={lazyState.sortOrder}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          paginatorClassName="custom-paginator"
          emptyMessage={emptyMessage}
          stripedRows
          showGridlines
          resizableColumns
          columnResizeMode="fit"
          scrollable
          scrollHeight={tableHeight}
        >
          {columnsToRender.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              body={col.body}
              sortable={col.sortable}
              style={col.style}
              exportable={col.exportable}
            />
          ))}
        </DataTable>
      </div>

      {/* ✅ ADDED: Global CSS for DataTable styling and paginator */}
      <style jsx global>{`
        /* Custom Paginator Styling */
        .custom-paginator {
          padding: 0.25rem 0.5rem !important;
          min-height: 2rem !important;
          height: 2rem !important;
          font-size: 0.75rem !important;
          background: #f8fafc !important;
          border-top: 1px solid #e2e8f0 !important;
        }

        /* Paginator elements height/width adjustments */
        .custom-paginator .p-paginator-pages .p-paginator-page {
          width: 1.75rem !important;
          height: 1.75rem !important;
          min-width: 1.75rem !important;
          font-size: 0.75rem !important;
          padding: 0 !important;
          margin: 0 0.125rem !important;
        }

        .custom-paginator .p-paginator-first,
        .custom-paginator .p-paginator-prev,
        .custom-paginator .p-paginator-next,
        .custom-paginator .p-paginator-last {
          width: 1.75rem !important;
          height: 1.75rem !important;
          min-width: 1.75rem !important;
          padding: 0 !important;
          margin: 0 0.125rem !important;
        }

        /* Rows per page dropdown sizing */
        .custom-paginator .p-dropdown {
          min-height: 1.75rem !important;
          height: 1.75rem !important;
          font-size: 0.75rem !important;
          padding: 0.25rem 0.5rem !important;
          min-width: 4rem !important;
          width: auto !important;
        }

        .custom-paginator .p-dropdown .p-dropdown-label {
          padding: 0.25rem 0.5rem !important;
          font-size: 0.75rem !important;
          line-height: 1.25 !important;
        }

        /* Current page report styling */
        .custom-paginator .p-paginator-current {
          font-size: 0.75rem !important;
          padding: 0.25rem 0.5rem !important;
          margin: 0 0.5rem !important;
          white-space: nowrap !important;
        }

        /* DataTable header font size */
        .p-datatable .p-datatable-thead > tr > th {
          font-size: 0.75rem !important;
          font-weight: 600 !important;
          padding: 0.5rem !important;
        }

        /* DataTable body font size */
        .p-datatable .p-datatable-tbody > tr > td {
          font-size: 0.75rem !important;
          padding: 0.375rem 0.5rem !important;
        }

        /* Paginator font size */
        .p-paginator {
          font-size: 0.75rem !important;
        }

        .p-paginator .p-paginator-pages .p-paginator-page,
        .p-paginator .p-dropdown,
        .p-paginator .p-paginator-current {
          font-size: 0.75rem !important;
        }

        /* DataTable loading and empty message */
        .p-datatable .p-datatable-loading-overlay .p-datatable-loading-icon,
        .p-datatable .p-datatable-tbody > tr.p-datatable-emptymessage > td {
          font-size: 0.75rem !important;
        }

        /* Tooltip font size */
        .p-tooltip .p-tooltip-text {
          font-size: 0.625rem !important;
        }

        /* Sort icons */
        .p-datatable .p-datatable-thead > tr > th .p-sortable-column-icon {
          font-size: 0.625rem !important;
        }

        /* Mobile responsive font sizes */
        @media (max-width: 768px) {
          .custom-paginator {
            padding: 0.1875rem 0.375rem !important;
            min-height: 1.75rem !important;
            height: 1.75rem !important;
            font-size: 0.6875rem !important;
          }

          .custom-paginator .p-paginator-pages .p-paginator-page,
          .custom-paginator .p-paginator-first,
          .custom-paginator .p-paginator-prev,
          .custom-paginator .p-paginator-next,
          .custom-paginator .p-paginator-last {
            width: 1.5rem !important;
            height: 1.5rem !important;
            min-width: 1.5rem !important;
            font-size: 0.6875rem !important;
          }

          .custom-paginator .p-dropdown {
            min-height: 1.5rem !important;
            height: 1.5rem !important;
            font-size: 0.6875rem !important;
            min-width: 3.5rem !important;
          }

          .custom-paginator .p-paginator-current {
            font-size: 0.6875rem !important;
            padding: 0.1875rem 0.375rem !important;
          }

          .p-datatable .p-datatable-thead > tr > th {
            font-size: 0.6875rem !important;
            padding: 0.375rem !important;
          }

          .p-datatable .p-datatable-tbody > tr > td {
            font-size: 0.6875rem !important;
            padding: 0.25rem 0.375rem !important;
          }

          .p-paginator {
            font-size: 0.6875rem !important;
          }

          .p-paginator .p-paginator-pages .p-paginator-page,
          .p-paginator .p-dropdown,
          .p-paginator .p-paginator-current {
            font-size: 0.6875rem !important;
          }
        }

        @media (max-width: 480px) {
          .custom-paginator {
            padding: 0.125rem 0.25rem !important;
            min-height: 1.5rem !important;
            height: 1.5rem !important;
            font-size: 0.625rem !important;
          }

          .custom-paginator .p-paginator-pages .p-paginator-page,
          .custom-paginator .p-paginator-first,
          .custom-paginator .p-paginator-prev,
          .custom-paginator .p-paginator-next,
          .custom-paginator .p-paginator-last {
            width: 1.25rem !important;
            height: 1.25rem !important;
            min-width: 1.25rem !important;
            font-size: 0.625rem !important;
          }

          .custom-paginator .p-dropdown {
            min-height: 1.25rem !important;
            height: 1.25rem !important;
            font-size: 0.625rem !important;
            min-width: 3rem !important;
          }

          .custom-paginator .p-paginator-current {
            font-size: 0.625rem !important;
            padding: 0.125rem 0.25rem !important;
            margin: 0 0.25rem !important;
          }

          .p-datatable .p-datatable-thead > tr > th {
            font-size: 0.625rem !important;
            padding: 0.25rem !important;
          }

          .p-datatable .p-datatable-tbody > tr > td {
            font-size: 0.625rem !important;
            padding: 0.1875rem 0.25rem !important;
          }

          .p-paginator {
            font-size: 0.625rem !important;
          }
        }
      `}</style>

      {isTimelineVisible && selectedPayment && (
        <StatusTimeline
          payment={selectedPayment}
          onClose={() => setTimelineVisible(false)}
        />
      )}

      <Dialog
        header="Transaction Details"
        visible={isDetailsVisible}
        onHide={() => setDetailsVisible(false)}
        style={{ width: "90vw", maxWidth: "800px" }}
        maximizable
        modal
      >
        {renderPaymentDetails()}
      </Dialog>
    </PrimeReactProvider>
  );
}
